import { employerAccountingApiV1 } from "./client";

export type OrderStatus = "new" | "paid" | "activated" | "cancelled";
export type PaymentType = "bill" | "balance";
export type Rate = "basic" | "optimum";
export type ResumeQuotaRate = "resume_quota_3_days" | "resume_quota_7_days" | "resume_quota_30_days";

export interface Order {
  id: string;
  status: OrderStatus;
  autoActivate: boolean;
  payerId: string;
  paymentType: PaymentType;
  service: string;
}

export interface OrderPosition {
  id: string;
  amount: number;
}

export interface VacancyQuotaOrderPosition extends OrderPosition {
  type: "vacancy_quota";
  rate: Rate;
  price: number;
  count: number;
  quotaActiveDays?: number;
}

export interface ResumeQuotaOrderPosition extends OrderPosition {
  type: "resume_quota";
  rate: ResumeQuotaRate;
  price: number;
  count: number;
  quotaActiveDays?: number;
}

export interface CreateOrder {
  payerId: string;
  paymentType: PaymentType;
  autoActivate: boolean;
  positions: Array<CreateVacancyQuotaOrderPosition|CreateResumeQuotaOrderPosition>;
}

export interface CreateVacancyQuotaOrderPosition {
  type: "vacancy_quota";
  rate: Rate;
  count: number;
}

export interface CreateResumeQuotaOrderPosition {
  type: "resume_quota";
  rate: ResumeQuotaRate;
  count: number;
}

export const createOrder = async (command: CreateOrder): Promise<void> => {
  const baseCount = command.positions
    .filter((p) => p.type === "vacancy_quota" && p.rate === "basic")
    .map(p => p.count)
    .reduce((a, c) => a + c, 0);

  const optimumCount = command.positions
    .filter((p) => p.type === "vacancy_quota" && p.rate === "optimum")
    .map(p => p.count)
    .reduce((a, c) => a + c, 0);

  const resumeQuota3DaysCount = command.positions
      .filter((p) => p.type === "resume_quota" && p.rate === "resume_quota_3_days")
      .map(p => p.count)
      .reduce((a, c) => a + c, 0);

  const resumeQuota7DaysCount = command.positions
      .filter((p) => p.type === "resume_quota" && p.rate === "resume_quota_7_days")
      .map(p => p.count)
      .reduce((a, c) => a + c, 0);

  const resumeQuota30DaysCount = command.positions
      .filter((p) => p.type === "resume_quota" && p.rate === "resume_quota_30_days")
      .map(p => p.count)
      .reduce((a, c) => a + c, 0);

  const hasVacancyQuota = baseCount > 0 || optimumCount > 0;
  const hasResumeQuota = resumeQuota3DaysCount > 0 ||
      resumeQuota7DaysCount > 0 ||
      resumeQuota30DaysCount > 0;

  let serviceDescription = '';

  if (hasVacancyQuota && hasResumeQuota) {
    serviceDescription =
        'Предоставление возможности публикации предложений о трудоустройстве (вакансий) с использованием сайта Raberu. ' +
        'Данная услуга включает размещение вакансий, их продвижение, а также доступ к базе резюме соискателей. ' +
        'Дополнительно предоставляются инструменты аналитики и управления откликами, что позволяет эффективно закрывать позиции в кратчайшие сроки. ' +
        'Также предоставляется доступ к контактам соискателей для оперативного поиска и привлечения кандидатов.';
  } else if (hasVacancyQuota) {
    serviceDescription =
        'Предоставление возможности публикации предложений о трудоустройстве (вакансий) с использованием сайта Raberu. ' +
        'Данная услуга включает размещение вакансий, их продвижение, а также доступ к базе резюме соискателей. ' +
        'Дополнительно предоставляются инструменты аналитики и управления откликами, что позволяет эффективно закрывать позиции в кратчайшие сроки.';
  } else if (hasResumeQuota) {
    serviceDescription =
        'Предоставление доступа к контактам соискателей на сайте Raberu. ' +
        'Услуга позволяет просматривать контактную информацию кандидатов. ' +
        'Доступ к контактам предоставляется на выбранный период и включает необходимое количество контактов для эффективного поиска сотрудников.';
  }

  await employerAccountingApiV1.order.createQuotaOrder({
    payerId: command.payerId,
    service: serviceDescription,
    baseCount: baseCount,
    optimumCount: optimumCount,
    resumeQuota3DaysCount: resumeQuota3DaysCount,
    resumeQuota7DaysCount: resumeQuota7DaysCount,
    resumeQuota30DaysCount: resumeQuota30DaysCount,
    paymentOption: command.paymentType,
    autoActivate: command.autoActivate,
  });
};

export const activateOrder = async (orderId: string): Promise<void> => {
  await employerAccountingApiV1.order.activateOrder(orderId);
};

export const cancelOrder = async (orderId: string): Promise<void> => {
    await employerAccountingApiV1.order.cancelOrder(orderId);
};
